import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "../../../../components/table";
import { TooltipProvider } from "../../../../components/tooltip";
import { getDemoEventCampaignMap } from "../../../campaign/api/campaign.api";
import type { EventFilters, EventListForAdminResponse } from "../../types/event.types";
import { buildEventListColumns } from "./eventListTableColumns";
import type { DataTableOption, DataTableSortDirection } from "../../../../components/table";

interface EventListViewProps {
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  events: EventListForAdminResponse[];
  filters: EventFilters;
  updateFilter: React.Dispatch<React.SetStateAction<EventFilters>>;
  dateSortDirection: DataTableSortDirection | null;
  onDateSort: (nextDirection: DataTableSortDirection | null) => void;
  categoryOptions: DataTableOption[];
  isFilterPopupOpen: (key: string) => boolean;
  handleFilterPopupChange: (key: string, open: boolean) => void;
  onRowOpen: (eventItem: EventListForAdminResponse) => void;
  onEdit: (eventItem: EventListForAdminResponse) => void;
  onDelete: (eventItem: EventListForAdminResponse) => void;
  onQRCode?: (eventItem: EventListForAdminResponse) => void;
  getEventStatus: (eventItem: EventListForAdminResponse) => string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const EventListView: React.FC<EventListViewProps> = ({
  loading,
  error = null,
  onRetry,
  events,
  filters,
  updateFilter,
  dateSortDirection,
  onDateSort,
  categoryOptions,
  isFilterPopupOpen,
  handleFilterPopupChange,
  onRowOpen,
  onEdit,
  onDelete,
  onQRCode,
  getEventStatus,
  page,
  totalPages,
  onPageChange,
}) => {
  const { t } = useTranslation();
  const demoEventCampaignMap = useMemo(() => getDemoEventCampaignMap(), []);

  const columns = useMemo(
    () =>
      buildEventListColumns({
        t,
        filters,
        updateFilter,
        categoryOptions,
        isFilterPopupOpen,
        handleFilterPopupChange,
        dateSortDirection,
        onDateSort,
        onRowOpen,
        onEdit,
        onDelete,
        onQRCode,
        demoEventCampaignMap,
      }),
    [
      t,
      filters,
      updateFilter,
      categoryOptions,
      isFilterPopupOpen,
      handleFilterPopupChange,
      dateSortDirection,
      onDateSort,
      onRowOpen,
      onEdit,
      onDelete,
      onQRCode,
      demoEventCampaignMap,
    ],
  );

  return (
    <div className="relative">
      <TooltipProvider delayDuration={150}>
        <DataTable
          columns={columns}
          dataSource={events}
          rowKey="id"
          loading={loading}
          loadingLabel={t("event.loading_events")}
          error={error}
          onRetry={onRetry}
          emptyLabel={t("event.no_events_available")}
          containerClassName="max-h-[calc(100vh-24rem)] overflow-auto"
          tableClassName="min-w-max"
          pagination={{
            page,
            totalPages,
            onPageChange,
          }}
          onRowClick={onRowOpen}
        />
      </TooltipProvider>
    </div>
  );
};

export default EventListView;
